import React from 'react';
import { ChevronRight, Hand, Footprints, Trophy } from 'lucide-react';

interface IntroStepProps {
  onNext: () => void;
  userName: string;
}

const IntroStep: React.FC<IntroStepProps> = ({ onNext, userName }) => {
  return (
    <div className="h-full flex flex-col text-center animate-fade-in relative">
      <style>{`
        @keyframes wave-custom {
          0% { transform: rotate(0deg); }
          10% { transform: rotate(18deg); }
          20% { transform: rotate(-10deg); }
          30% { transform: rotate(14deg); }
          40% { transform: rotate(-6deg); }
          50% { transform: rotate(10deg); }
          60% { transform: rotate(0deg); }
          100% { transform: rotate(0deg); }
        }
        .animate-wave-custom {
          animation: wave-custom 2.5s infinite;
          transform-origin: 70% 70%;
          display: block; /* Ensure transform applies */
        }
      `}</style>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-4 py-2 custom-scrollbar pb-24 md:pb-0 md:h-full md:overflow-hidden">
        <div className="space-y-6 md:space-y-0 py-2 md:h-full md:grid md:grid-cols-2 md:gap-12 md:items-center md:px-8">

          {/* Left Column: Hero */}
          <div className="flex flex-col items-center md:items-start md:text-left space-y-6 md:space-y-8">
            <div className="relative w-24 h-24 md:w-40 md:h-40 mx-auto md:mx-0 group animate-slide-up">
              <div className="absolute inset-0 bg-indigo-500/20 rounded-full blur-2xl animate-pulse-slow"></div>
              <div className="relative w-24 h-24 md:w-40 md:h-40 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center shadow-lg shadow-indigo-500/30 rotate-3 transition-transform group-hover:rotate-6 duration-300">
                <Hand className="w-12 h-12 md:w-20 md:h-20 text-white animate-wave-custom" />
              </div>
            </div>

            <div className="space-y-3 md:space-y-6 animate-slide-up [animation-delay:100ms]">
              <h2 className="text-3xl sm:text-4xl md:text-6xl font-extrabold text-slate-900 tracking-tight">
                Xin chào, <br className="hidden md:block" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">{userName}</span>!
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto md:mx-0 leading-relaxed text-sm sm:text-lg md:text-xl px-2 md:px-0">
                Chào mừng bạn đến với <strong>TechCoop Sports Challenge</strong>. Nơi ghi nhận nỗ lực và vinh danh tinh thần thể thao.
              </p>
            </div>
          </div>

          {/* Right Column: Features & Action */}
          <div className="space-y-6 md:space-y-10">
            <div className="grid grid-cols-1 gap-3 md:gap-6 max-w-3xl mx-auto md:mx-0 text-left animate-slide-up [animation-delay:200ms]">
              <div className="bg-white/60 backdrop-blur-md p-5 md:p-6 rounded-2xl md:rounded-3xl border border-white/50 hover:bg-white/80 transition-all group cursor-default shadow-sm hover:shadow-md flex items-center gap-4">
                <div className="w-10 h-10 md:w-14 md:h-14 bg-green-100 text-green-600 rounded-xl md:rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shrink-0">
                  <Footprints className="w-5 h-5 md:w-7 md:h-7" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-slate-800">Ghi nhận tự động</h3>
                  <p className="text-slate-600 mt-1 text-sm md:text-base leading-relaxed">Đồng bộ hoạt động từ Strava.</p>
                </div>
              </div>
              <div className="bg-white/60 backdrop-blur-md p-5 md:p-6 rounded-2xl md:rounded-3xl border border-white/50 hover:bg-white/80 transition-all group cursor-default shadow-sm hover:shadow-md flex items-center gap-4">
                <div className="w-10 h-10 md:w-14 md:h-14 bg-yellow-100 text-yellow-600 rounded-xl md:rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shrink-0">
                  <Trophy className="w-5 h-5 md:w-7 md:h-7" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-slate-800">Thi đua Realtime</h3>
                  <p className="text-slate-600 mt-1 text-sm md:text-base leading-relaxed">BXH cập nhật liên tục.</p>
                </div>
              </div>
            </div>

            {/* Desktop Button */}
            <div className="hidden md:block animate-slide-up [animation-delay:300ms]">
              <button
                onClick={onNext}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-xl font-bold py-5 px-8 rounded-2xl transition-all flex items-center justify-center gap-3 shadow-xl shadow-indigo-500/20 group hover:-translate-y-1 hover:shadow-indigo-500/30"
              >
                Bắt đầu ngay
                <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Footer (Mobile Only) */}
      <div className="flex-none p-4 w-full bg-white/80 backdrop-blur-md border-t border-slate-100 md:hidden z-10 animate-slide-up [animation-delay:300ms]">
        <button
          onClick={onNext}
          className="w-full max-w-md mx-auto bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-lg font-bold py-4 px-8 rounded-2xl transition-all flex items-center justify-center gap-3 shadow-xl shadow-indigo-500/20 group hover:-translate-y-1"
        >
          Bắt đầu ngay
          <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </div>
  );
};

export default IntroStep;
